import Badge from '@shared/components/Badge';
import type { Tone } from '@shared/lib/tones';

/**
 * Platform-admin status pill — maps a backend status string to a semantic tone.
 *
 * The shared `Badge` owns geometry and tones; this owns the admin's status
 * vocabulary (subscriptions, workshop lifecycle, invitations, roles). Keeping
 * the mapping here rather than in `@shared` means the tenant never sees keys
 * like `past_due`, and the union stays semantic for everyone.
 *
 * @example
 * <StatusBadge status="past_due" />
 * <StatusBadge status="suspended" label="Suspendido" />
 */

const STATUS_TONES: Record<string, Tone> = {
    // Subscription lifecycle
    active: 'success',
    trial: 'info',
    suspended: 'danger',
    cancelled: 'neutral',
    past_due: 'warning',
    paused: 'neutral',
    pending: 'warning',
    accepted: 'success',
    expired: 'neutral',
    pending_deletion: 'danger',
    // Workshop access/lifecycle model (see documentation/Workshop_Lifecycle.md)
    full: 'success',
    read_only: 'warning',
    blocked: 'danger',
    scheduled_deletion: 'warning',
    billing_exempt: 'info',
    manual_hold: 'danger',
    inactive: 'neutral',
    // GoTrue identity that has no local profile yet (incomplete signup)
    unlinked: 'warning',
    deleted: 'danger',
    // Roles
    platform_admin: 'purple',
    owner: 'primary',
    admin: 'purple',
    member: 'info',
    viewer: 'neutral',
};

/** Spanish label for every key the admin renders. Without this a badge with
 *  no `label` printed the raw English key (`past_due`, `pending deletion`),
 *  and colour was the only signal. Unknown keys still fall back to the key
 *  with underscores as spaces — but that is now a bug to fix here, not a look. */
const ES_LABELS: Record<string, string> = {
    active: 'Activa',
    trial: 'En prueba',
    trialing: 'En prueba',
    suspended: 'Suspendida',
    cancelled: 'Cancelada',
    past_due: 'Pago vencido',
    paused: 'Pausada',
    pending: 'Pendiente',
    accepted: 'Aceptada',
    expired: 'Expirada',
    incomplete: 'Sin completar',
    pending_deletion: 'Pendiente de borrado',
    full: 'Acceso completo',
    read_only: 'Solo lectura',
    blocked: 'Bloqueado',
    scheduled_deletion: 'Borrado programado',
    billing_exempt: 'Exento de facturación',
    manual_hold: 'Bloqueo manual',
    inactive: 'Inactivo',
    inactivo: 'Inactivo',
    unlinked: 'Sin perfil',
    deleted: 'Borrado',
    platform_admin: 'Admin de plataforma',
    owner: 'Propietario',
    admin: 'Admin',
    member: 'Miembro',
    worker: 'Trabajador',
    viewer: 'Solo lectura',
    processed: 'Procesado',
    error: 'Error',
    sent: 'Enviado',
    delivered: 'Entregado',
    bounced: 'Rebotado',
    failed: 'Fallido',
    queued: 'En cola',
    open: 'Abierto',
    closed: 'Cerrado',
    resolved: 'Resuelto',
    draft: 'Borrador',
    published: 'Publicado',
    archived: 'Archivado',
    none: 'Sin suscripción',
    scheduled_cancel: 'Cancela a fin de ciclo',
    stale_cancel: 'Cancelada · pendiente de confirmar',
};

interface StatusBadgeProps {
    /** Backend status key. Unknown keys fall back to the neutral tone. */
    status: string;
    /** Override the displayed label. Defaults to the key with underscores as spaces. */
    label?: string;
    className?: string;
}

export default function StatusBadge({ status, label, className = '' }: StatusBadgeProps) {
    // Guard against callers passing `status={undefined}` (TS says required but
    // real data — e.g. legacy catalog rows with a null status — can violate it).
    // Before this guard `.replace` on undefined crashed the page (Sentry
    // ADMIN-FRONTEND-4).
    const displayLabel = label ?? (status ? (ES_LABELS[status] ?? status.replace(/_/g, ' ')) : '');
    return (
        <Badge tone={STATUS_TONES[status] ?? 'neutral'} className={className} title={displayLabel}>
            {displayLabel}
        </Badge>
    );
}
